/**
 * "Divide into sections" panel — a docked card, NOT a modal, deliberately: the
 * preview IS the table (same color = same section, painted by
 * SectionsOverlayLayer), so the accept/cancel UI must leave the table visible
 * instead of covering it the way the arrange dialog covers its column.
 *
 * Flow (docs/08 §2's explicit-spend rule — F-70's translate button set the
 * precedent): opening the panel never calls the server. "Analyze script" is
 * the one paid action; it reads EVERY non-empty Original Script row (the
 * the top-bar menu only opened the panel), asks the model to group them, and
 * holds the answer in the store for the overlay + this list to render. "Add
 * section column" then applies it in one `session.batch` — one transaction,
 * one sync unit, one undo step — via apply.ts.
 *
 * The model picker is the shared one (features/ai-models) — the same list,
 * the same "Default · <model>" first option and the same remembered-pick rule
 * as every other AI dropdown in Kola.
 */
import { findColumnByName } from "@kola/shared";
import { useEffect, useRef, useState } from "react";
import { useStore } from "zustand";

import { ApiError, suggestSections } from "../../core/api.js";
import { newId } from "../../core/ids.js";
import type { BoardSession } from "../../core/session.js";
import { CloseButton } from "../../ui/CloseButton.js";
import { showToast } from "../../ui/toast.js";
import { ModelSelect } from "../ai-models/ModelSelect.js";
import { DEFAULT_MODEL, rememberModel, storedModel } from "../ai-models/pick.js";
import {
  collectScriptRows,
  modelFromApiError,
  moodExcerpt,
  sectionsErrorMessage,
  toPreviewMoods,
  toPreviewSections,
} from "./analyze.js";
import {
  applySections,
  BGM_COLUMN_NAME,
  MOOD_COLUMN_NAME,
  SECTION_COLUMN_NAME,
  type ApplySectionsResult,
} from "./apply.js";
import { sectionsUi, type SectionsTarget } from "./store.js";

export interface SectionsLayerProps {
  session: BoardSession;
}

export function SectionsLayer({ session }: SectionsLayerProps) {
  const target = useStore(sectionsUi, (s) => s.target);

  // The panel (and its overlay's analysis) must never survive leaving the
  // board it names.
  useEffect(() => {
    return () => sectionsUi.getState().close();
  }, []);

  const forThisBoard = target !== null && target.boardId === session.boardId;
  return forThisBoard ? <SectionsPanel session={session} target={target} /> : null;
}

function SectionsPanel({ session, target }: { session: BoardSession; target: SectionsTarget }) {
  const close = () => sectionsUi.getState().close();
  const loading = useStore(sectionsUi, (s) => s.loading);
  const error = useStore(sectionsUi, (s) => s.error);
  const analysis = useStore(sectionsUi, (s) => s.analysis);

  const [model, setModel] = useState<string>(() => storedModel("divide-sections"));
  // F-126 (2026-09-03): the BGM column is no longer a choice here — a divided
  // script always has its music slots (apply.ts mints it; and a board divided
  // before the slots existed grows the column on its own, BgmSlotLayer).
  // F-139: neither is the Mood column — it IS the output (the mood as text,
  // the topic as color), so there is nothing left to tick.
  /** Guards a response landing after the panel closed (no setState on unmount). */
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const analyze = async () => {
    if (loading) return;
    const ui = sectionsUi.getState();
    // Rows are read at ANALYZE time, not open time — the board is live.
    const collected = collectScriptRows(session.board, target.sheetId);
    if (!collected.ok) {
      ui.setError(collected.message);
      return;
    }
    ui.setLoading(true);
    ui.setError(null);
    try {
      const response = await suggestSections(
        session.boardId,
        collected.rows,
        model === DEFAULT_MODEL ? undefined : model,
      );
      if (!alive.current) return;
      sectionsUi.getState().setAnalysis({
        sections: toPreviewSections(response.sections),
        moods: toPreviewMoods(response, collected.rows),
        model: response.model,
      });
    } catch (err) {
      if (!alive.current) return;
      const message =
        err instanceof ApiError
          ? sectionsErrorMessage(err.status, err.code, modelFromApiError(err))
          : "Couldn't analyze the script. Try again.";
      sectionsUi.getState().setError(message);
    } finally {
      if (alive.current) sectionsUi.getState().setLoading(false);
    }
  };

  const apply = () => {
    if (analysis === null) return;
    let result: ApplySectionsResult | null = null;
    session.batch(() => {
      result = applySections(session.board, target.sheetId, analysis.sections, () => `c${newId(10)}`, {
        moods: analysis.moods,
      });
    });
    // F-45: while frozen, session.batch never runs its callback — report that
    // honestly instead of pretending the column appeared.
    if (result === null) {
      showToast("Offline — nothing was changed");
    } else {
      const applied: ApplySectionsResult = result;
      // F-139: the spans after the first each begin on a row the mood changed.
      const moodChanges = Math.max(0, analysis.moods.length - 1);
      const parts = [
        `${applied.moodColReused ? "Rewrote" : "Added"} the "${MOOD_COLUMN_NAME}" column — ${applied.labeled} row${
          applied.labeled === 1 ? "" : "s"
        }, ${analysis.sections.length} topic${analysis.sections.length === 1 ? "" : "s"} by color, ${moodChanges} mood change${moodChanges === 1 ? "" : "s"}`,
      ];
      if (applied.bgmColId !== undefined) {
        parts.push(`"${BGM_COLUMN_NAME}" column ready — 2 stems per mood`);
      }
      if (applied.sectionColRemoved) {
        parts.push(`"${SECTION_COLUMN_NAME}" column removed — the topic is the color now`);
      }
      if (applied.movedStems > 0) {
        parts.push(
          `${applied.movedStems} stem${applied.movedStems === 1 ? "" : "s"} moved to the first row of ${
            applied.movedStems === 1 ? "its" : "their"
          } mood`,
        );
      }
      if (applied.skipped > 0) {
        parts.push(`${applied.skipped} deleted meanwhile — skipped`);
      }
      showToast(parts.join(" · "));
    }
    close();
  };

  const rowCount = (rowIds: readonly string[]) =>
    `${rowIds.length} row${rowIds.length === 1 ? "" : "s"}`;

  // Dividing AGAIN (2026-09-11): the button and the note say so up front,
  // because applying then rewrites the Mood column in place and, on a board
  // divided before F-139, removes its Section column (apply.ts). Read at
  // render — a column appearing while the panel sits open is rare, and the
  // apply itself re-reads the doc.
  const hasMoodColumn = findColumnByName(session.board, target.sheetId, MOOD_COLUMN_NAME) !== undefined;
  const hasSectionColumn =
    findColumnByName(session.board, target.sheetId, SECTION_COLUMN_NAME) !== undefined;
  const dividedBefore = hasMoodColumn || hasSectionColumn;

  return (
    <div className="sections-panel" role="dialog" aria-label="Divide into sections">
      <div className="sections-head">
        <span className="sections-title">Divide into sections</span>
        <span className="spacer" />
        <CloseButton onClick={close} />
      </div>

      {analysis === null ? (
        <p className="sections-note">
          AI reads the whole Original Script column and does two things: it groups the rows
          into topics — always marking the <strong>Intro</strong>, the <strong>Payoff</strong>{" "}
          and the <strong>Outro</strong> — and it marks <strong>where the mood changes</strong>,
          on the exact line. A joke in the middle of a topic is a mood change, not a new topic:
          the lines before it, the joke and the lines after stay one topic, one color. Applying
          adds one <strong>Mood</strong> column — the mood as its text, the topic as its color —
          and a <strong>BGM</strong> column with one music slot per mood.
          {dividedBefore && (
            <>
              {" "}
              This script was divided before: applying rewrites the Mood column in place
              {hasSectionColumn && " and removes the old Section column"}; music already chosen
              stays and follows the new moods.
            </>
          )}{" "}
          Nothing is written until you apply.
        </p>
      ) : (
        <p className="sections-note">
          Same color = same topic; each dashed line marks the row where the mood changes. Check
          both on the table. Analyzed by <strong>{analysis.model}</strong>.
        </p>
      )}

      {analysis !== null && (
        <div className="sections-list">
          {analysis.sections.map((section, index) => (
            <div key={index} className="sections-item">
              <span className="sections-swatch" style={{ background: section.color }} />
              <span className="sections-item-text">
                <span className="sections-item-title">{section.title}</span>
              </span>
              <span className="sections-item-count">{rowCount(section.rowIds)}</span>
            </div>
          ))}
          {analysis.moods.length > 0 ? (
            <div className="sections-moods" aria-label="Mood changes">
              <span className="sections-hint">Mood — and the line each one starts on</span>
              {analysis.moods.map((span, index) => {
                const first = span.rowIds[0];
                const quote =
                  first === undefined ? "" : moodExcerpt(session.board, target.sheetId, first);
                return (
                  <div key={index} className="sections-item">
                    <span className="sections-mood-chip">{span.mood}</span>
                    <span className="sections-item-text sections-mood-quote" title={quote}>
                      {quote === "" ? "(line deleted)" : `“${quote}”`}
                    </span>
                    <span className="sections-item-count">{rowCount(span.rowIds)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="sections-note">
              The model marked no mood changes — a Mood column would stay empty.
            </p>
          )}
        </div>
      )}

      <label className="sections-model" title="Which model analyzes">
        <span className="sections-hint">Model</span>
        <ModelSelect
          functionKey="divide-sections"
          kind="text"
          value={model}
          disabled={loading}
          onChange={(next) => {
            setModel(next);
            rememberModel("divide-sections", next);
          }}
        />
      </label>

      {error !== null && (
        <p className="sections-error" role="alert">
          {error}
        </p>
      )}

      <div className="sections-actions">
        <button type="button" className="btn" onClick={close}>
          Cancel
        </button>
        {analysis !== null && (
          <button type="button" className="btn" disabled={loading} onClick={() => void analyze()}>
            {loading ? "Analyzing…" : "Analyze again"}
          </button>
        )}
        {analysis === null ? (
          <button
            type="button"
            className="btn btn-primary"
            disabled={loading}
            onClick={() => void analyze()}
          >
            {loading ? "Analyzing…" : "Analyze script"}
          </button>
        ) : (
          <button type="button" className="btn btn-primary" disabled={loading} onClick={apply}>
            {dividedBefore ? "Replace Mood column" : "Add Mood column"}
          </button>
        )}
      </div>
    </div>
  );
}
